// Mutation hooks for Disease Code Management.
// Slice 8 — group CRUD. Slice 9 adds code CRUD and the optimistic status toggle.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { diseaseCodeApi } from '../api/disease-code.adapter';
import type {
  CreateDiseaseCodeInput,
  CreateDiseaseGroupInput,
  DiseaseCode,
  DiseaseCodeStatus,
  DiseaseGroup,
  UpdateDiseaseCodeInput,
  UpdateDiseaseGroupInput,
} from '../types/disease-code.types';
import { diseaseCodeQueryKeys } from './use-disease-groups';

export function useCreateDiseaseGroup() {
  const qc = useQueryClient();
  return useMutation<DiseaseGroup, Error, CreateDiseaseGroupInput>({
    mutationFn: (input) => diseaseCodeApi.createGroup(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.groups() });
    },
  });
}

export function useUpdateDiseaseGroup() {
  const qc = useQueryClient();
  return useMutation<DiseaseGroup, Error, { id: number; input: UpdateDiseaseGroupInput }>({
    mutationFn: ({ id, input }) => diseaseCodeApi.updateGroup(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.groups() });
    },
  });
}

export function useDeleteDiseaseGroup() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => diseaseCodeApi.deleteGroup(id),
    // PRD §4 — group delete cascades into codes; nuke every cached `codes` list
    // so any open right panel re-fetches if the active group was deleted.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.all });
    },
  });
}

export function useCreateDiseaseCode() {
  const qc = useQueryClient();
  return useMutation<DiseaseCode, Error, { groupId: number; input: CreateDiseaseCodeInput }>({
    mutationFn: ({ groupId, input }) => diseaseCodeApi.createCode(groupId, input),
    onSuccess: (_data, { groupId }) => {
      // Refresh both the group list (codeCount changed) and the affected codes list.
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.groups() });
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.codes(groupId) });
    },
  });
}

export function useUpdateDiseaseCode() {
  const qc = useQueryClient();
  return useMutation<
    DiseaseCode,
    Error,
    { id: number; groupId: number; input: UpdateDiseaseCodeInput }
  >({
    mutationFn: ({ id, input }) => diseaseCodeApi.updateCode(id, input),
    onSuccess: (_data, { groupId }) => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.codes(groupId) });
    },
  });
}

export function useDeleteDiseaseCode() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; groupId: number }>({
    mutationFn: ({ id }) => diseaseCodeApi.deleteCode(id),
    onSuccess: (_data, { groupId }) => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.groups() });
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.codes(groupId) });
    },
  });
}

// PRD 08 §3.1 / §6 — status toggle is immediate, no confirm. Optimistically
// flips the badge in the codes cache and rolls back on failure so the UI stays
// responsive even at high mock latency.
interface ToggleStatusVars {
  id: number;
  groupId: number;
  nextStatus: DiseaseCodeStatus;
}

interface ToggleStatusContext {
  prev?: DiseaseCode[];
  key: ReturnType<typeof diseaseCodeQueryKeys.codes>;
}

export function useToggleDiseaseCodeStatus() {
  const qc = useQueryClient();
  return useMutation<DiseaseCode, Error, ToggleStatusVars, ToggleStatusContext>({
    mutationFn: ({ id, nextStatus }) =>
      diseaseCodeApi.updateCode(id, { status: nextStatus }),
    onMutate: async ({ id, groupId, nextStatus }) => {
      const key = diseaseCodeQueryKeys.codes(groupId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DiseaseCode[]>(key);
      if (prev) {
        qc.setQueryData<DiseaseCode[]>(
          key,
          prev.map((c) => (c.id === id ? { ...c, status: nextStatus } : c)),
        );
      }
      return { prev, key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: (_data, _err, { groupId }) => {
      qc.invalidateQueries({ queryKey: diseaseCodeQueryKeys.codes(groupId) });
    },
  });
}
